import { useImperativeHandle, useRef, useState } from 'react';

import { InfoCircleFillIcon } from '@autopass-web/mochi-icons';

import { PAYMENT_METHODS_INFO, paymentMethodValues } from '../configs';
import { AnalyticsProvider, type EventRegistry } from '../hooks/analytics';
import {
  type AvailMethods,
  type ControlRef as PaymentRef,
  PaymentMethods,
} from './payment-methods';
import {
  ReceiptConfig,
  type ReceiptFormControlRef as ReceiptRef,
  type ReceiptInfo,
} from './receipt-config';
import { Hint } from './receipt-config/receipt-types/UI';

import type { PaymentMethodInfo, PaymentMethodTypes } from '../configs';
import type { SubmitResult } from '../hooks/use-bind-async-submit';
import type { ReceiptTypeConfig } from './receipt-config';
import type { ReactNode, RefObject } from 'react';

export type PaymentOptionsControlRef = {
  submit: () => Promise<{
    payment?: SubmitResult<PaymentMethodInfo>;
    receipt?: SubmitResult<ReceiptInfo>;
  }>;
};
const defaultMethods = paymentMethodValues.map((value) => ({
  value,
  available: true,
}));

const getReceiptTypeConfig = (
  method: PaymentMethodTypes | undefined,
): ReceiptTypeConfig | undefined => {
  if (!method) return undefined;

  return PAYMENT_METHODS_INFO[method].receiptConfig;
};

export const PaymentOptions = ({
  availMethods = defaultMethods,
  isReceiptEnabled = true,
  controlRef,
  validateBarcode,
  eventRegistry,
  analKey,
  methodHint,
  onMethodChange,
}: {
  availMethods?: AvailMethods;
  isReceiptEnabled?: boolean;
  controlRef: RefObject<PaymentOptionsControlRef>;
  validateBarcode?: (input: string) => Promise<boolean>;
  eventRegistry: EventRegistry;
  analKey?: string;
  methodHint?: (method: PaymentMethodTypes) => ReactNode;
  onMethodChange?: (method: PaymentMethodTypes | undefined) => void;
}) => {
  const [method, setMethod] = useState<PaymentMethodTypes>();
  const paymentRef = useRef<PaymentRef>(null);
  const receiptRef = useRef<ReceiptRef>(null);

  const availTypes = method
    ? PAYMENT_METHODS_INFO[method].availReceiptTypes
    : [];

  useImperativeHandle(
    controlRef,
    () => ({
      async submit() {
        const payment = await paymentRef.current?.submit();

        if (isReceiptEnabled) {
          const receipt = await receiptRef.current?.submit();

          return {
            payment,
            receipt,
          };
        }

        return {
          payment,
        };
      },
    }),
    [isReceiptEnabled, method],
  );

  const receiptConfig = getReceiptTypeConfig(method);

  const isAutopassNotAvailable =
    method === 'autopass' &&
    availMethods.some(({ value, available }) => {
      return value === 'autopass' && !available;
    });

  return (
    <AnalyticsProvider eventRegistry={eventRegistry} analKey={analKey}>
      <div>
        <PaymentMethods
          methods={availMethods}
          controlRef={paymentRef}
          onMethodChange={(newMethod) => {
            setMethod(newMethod);
            onMethodChange?.(newMethod);
          }}
          hint={methodHint}
        />
        {isAutopassNotAvailable && (
          <div className="flex gap-1 px-2 py-3 rounded-xl bg-[#F2F8FF] text-xs">
            <div>
              <InfoCircleFillIcon className="h-[15px] w-[15px]" />
            </div>
            <div>
              車麻吉 APP
              因未安裝/登入或未綁定信用卡，故無法使用自動返回原頁面，請選擇其他付款方式
            </div>
          </div>
        )}
        {isReceiptEnabled && (
          <ReceiptConfig
            controlRef={receiptRef}
            validateBarcode={validateBarcode}
            availTypes={availTypes}
            config={receiptConfig}
            hint={
              method === 'apple_pay' && (
                <Hint>
                  系統將統一寄送至 Apple Pay 設定的信箱中，故毋須輸入信箱。
                </Hint>
              )
            }
          />
        )}
      </div>
    </AnalyticsProvider>
  );
};
